const jessCoachingPlanHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Project Jess — 90-Day Star Server Transformation</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');

  :root {
    --navy: #0f1923;
    --dark: #1a2332;
    --card: #1e2d3d;
    --card-alt: #243447;
    --mint: #29bb89;
    --mint-light: #34d399;
    --mint-dim: rgba(41,187,137,0.12);
    --coral: #f87171;
    --coral-dim: rgba(248,113,113,0.10);
    --amber: #fbbf24;
    --amber-dim: rgba(251,191,36,0.10);
    --blue: #60a5fa;
    --blue-dim: rgba(96,165,250,0.10);
    --purple: #a78bfa;
    --purple-dim: rgba(167,139,250,0.10);
    --text: #e2e8f0;
    --muted: #94a3b8;
    --dim: #64748b;
    --border: #334155;
    --phone-bg: #000;
    --phone-screen: #111827;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'Inter', -apple-system, sans-serif;
    background: var(--navy);
    color: var(--text);
    line-height: 1.75;
    -webkit-font-smoothing: antialiased;
  }

  /* ============ HERO ============ */
  .hero {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 40px 32px 32px;
    position: relative;
    overflow: hidden;
    background: linear-gradient(170deg, #0f1923 0%, #162230 40%, #0f2923 100%);
  }
  .hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 600px 400px at 30% 60%, rgba(41,187,137,0.07), transparent),
      radial-gradient(ellipse 500px 300px at 75% 30%, rgba(96,165,250,0.05), transparent);
  }
  .hero-badge {
    position: relative;
    display: inline-block;
    padding: 6px 20px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--mint);
    background: var(--mint-dim);
    border: 1px solid rgba(41,187,137,0.25);
    margin-bottom: 24px;
  }
  .hero h1 {
    position: relative;
    font-size: clamp(2.4rem, 5vw, 4rem);
    font-weight: 900;
    letter-spacing: -0.04em;
    line-height: 1.1;
    background: linear-gradient(135deg, #fff 20%, var(--mint-light) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 16px;
  }
  .hero .subtitle {
    position: relative;
    font-size: 1.15rem;
    color: var(--muted);
    font-weight: 300;
    max-width: 600px;
    margin-bottom: 40px;
  }
  .hero-stats {
    position: relative;
    display: flex;
    gap: 48px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .hero-stat { text-align: center; }
  .hero-stat .now { font-size: 1.8rem; font-weight: 800; color: var(--coral); }
  .hero-stat .arrow { font-size: 1.2rem; color: var(--muted); margin: 0 4px; }
  .hero-stat .goal { font-size: 1.8rem; font-weight: 800; color: var(--mint); }
  .hero-stat .label { font-size: 0.75rem; color: var(--dim); text-transform: uppercase; letter-spacing: 0.1em; margin-top: 2px; }

  /* ============ NAVIGATION ============ */
  .nav {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(15,25,35,0.92);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--border);
    padding: 0;
  }
  .nav-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    overflow-x: auto;
    scrollbar-width: none;
    padding: 0 24px;
  }
  .nav-inner::-webkit-scrollbar { display: none; }
  .nav a {
    color: var(--muted);
    text-decoration: none;
    font-size: 0.82rem;
    font-weight: 500;
    padding: 16px 18px;
    white-space: nowrap;
    transition: color 0.2s;
    border-bottom: 2px solid transparent;
  }
  .nav a:hover { color: var(--mint); border-bottom-color: var(--mint); }

  /* ============ CONTAINER ============ */
  .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

  /* ============ PHASE HEADER ============ */
  .phase-header {
    padding: 80px 0 40px;
  }
  .phase-number {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    margin-bottom: 8px;
  }
  .phase-number.p1 { color: var(--mint); }
  .phase-number.p2 { color: var(--blue); }
  .phase-number.p3 { color: var(--purple); }
  .phase-number.p4 { color: var(--amber); }
  .phase-number.wild { color: var(--coral); }
  .phase-header h2 {
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    margin-bottom: 8px;
  }
  .phase-header .phase-sub {
    font-size: 1rem;
    color: var(--muted);
    font-weight: 300;
    font-style: italic;
  }
  .phase-header .phase-dates {
    display: inline-block;
    margin-top: 12px;
    padding: 4px 14px;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 600;
    background: var(--card);
    color: var(--dim);
    border: 1px solid var(--border);
  }

  /* ============ STEP BLOCK ============ */
  .step-block {
    display: flex;
    gap: 48px;
    align-items: flex-start;
    padding: 48px 0;
    border-bottom: 1px solid rgba(51,65,85,0.4);
  }
  .step-block:last-child { border-bottom: none; }
  .step-content { flex: 1; min-width: 0; }
  .step-phone { flex: 0 0 260px; position: sticky; top: 100px; }

  .step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 0.9rem;
    font-weight: 700;
    margin-bottom: 12px;
    color: var(--navy);
  }
  .step-num.p1 { background: var(--mint); }
  .step-num.p2 { background: var(--blue); }
  .step-num.p3 { background: var(--purple); }
  .step-num.p4 { background: var(--amber); }
  .step-num.wild { background: var(--coral); }

  .step-title {
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 12px;
    line-height: 1.3;
  }
  .step-body { color: var(--muted); margin-bottom: 16px; }
  .step-body strong { color: var(--text); font-weight: 600; }

  .why-box {
    background: var(--card);
    border-radius: 10px;
    padding: 16px 20px;
    border-left: 3px solid var(--dim);
    margin-top: 16px;
  }
  .why-box .why-label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--dim);
    margin-bottom: 4px;
  }
  .why-box p { color: var(--muted); font-size: 0.92rem; margin: 0; }

  /* ============ PHONE MOCKUP ============ */
  .phone {
    width: 240px;
    border-radius: 32px;
    background: #1a1a2e;
    padding: 12px;
    box-shadow:
      0 0 0 2px #2a2a4a,
      0 20px 60px rgba(0,0,0,0.5),
      inset 0 0 0 1px rgba(255,255,255,0.05);
    position: relative;
  }
  .phone-notch {
    width: 80px;
    height: 22px;
    background: #1a1a2e;
    border-radius: 0 0 14px 14px;
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  }
  .phone-screen {
    background: var(--phone-screen);
    border-radius: 22px;
    overflow: hidden;
    min-height: 420px;
    position: relative;
  }

  /* Phone status bar */
  .phone-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 18px 4px;
    font-size: 0.62rem;
    font-weight: 600;
    color: #fff;
  }
  .phone-status .time { font-weight: 700; }
  .phone-status .icons { display: flex; gap: 4px; align-items: center; }
  .phone-status .icons svg { width: 12px; height: 12px; fill: #fff; }

  /* App header */
  .app-header {
    padding: 8px 16px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .app-logo {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, var(--mint), #1a9d6f);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 0.7rem;
    color: #fff;
  }
  .app-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
  }

  /* Screen content */
  .screen-section {
    padding: 0 14px 12px;
  }
  .screen-label {
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--dim);
    margin-bottom: 6px;
  }
  .screen-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
    margin-bottom: 8px;
  }
  .screen-subtitle {
    font-size: 0.72rem;
    color: var(--muted);
    margin-bottom: 10px;
  }

  /* Screen cards */
  .screen-card {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 8px;
  }
  .screen-card-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .screen-card-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.82rem;
    flex-shrink: 0;
  }
  .screen-card-text { flex: 1; }
  .screen-card-title {
    font-size: 0.78rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
  }
  .screen-card-sub {
    font-size: 0.62rem;
    color: var(--muted);
    line-height: 1.3;
  }

  /* Screen stat row */
  .screen-stat-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .screen-stat-row:last-child { border-bottom: none; }
  .screen-stat-label { font-size: 0.68rem; color: var(--muted); }
  .screen-stat-val { font-size: 0.68rem; font-weight: 700; }

  /* Screen bar chart */
  .screen-bar-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  .screen-bar-label {
    font-size: 0.62rem;
    color: var(--muted);
    width: 55px;
    text-align: right;
    flex-shrink: 0;
  }
  .screen-bar-track {
    flex: 1;
    height: 14px;
    background: rgba(255,255,255,0.06);
    border-radius: 7px;
    overflow: hidden;
  }
  .screen-bar-fill {
    height: 100%;
    border-radius: 7px;
    transition: width 0.5s ease;
  }
  .screen-bar-val {
    font-size: 0.6rem;
    font-weight: 700;
    width: 32px;
    flex-shrink: 0;
  }

  /* Screen button */
  .screen-btn {
    display: block;
    width: 100%;
    padding: 10px;
    border: none;
    border-radius: 10px;
    font-size: 0.78rem;
    font-weight: 700;
    text-align: center;
    color: #fff;
    cursor: default;
    margin-top: 8px;
  }
  .screen-btn.mint { background: linear-gradient(135deg, var(--mint), #1fa870); }
  .screen-btn.blue { background: linear-gradient(135deg, var(--blue), #3b82f6); }
  .screen-btn.purple { background: linear-gradient(135deg, var(--purple), #8b5cf6); }
  .screen-btn.amber { background: linear-gradient(135deg, var(--amber), #f59e0b); color: #1a1a2e; }

  /* Screen progress ring */
  .screen-ring {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 12px auto;
  }

  /* Screen list */
  .screen-list-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .screen-list-item:last-child { border-bottom: none; }
  .screen-check {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    flex-shrink: 0;
  }
  .screen-check.done {
    background: var(--mint);
    border-color: var(--mint);
    color: #fff;
  }
  .screen-list-text {
    font-size: 0.72rem;
    color: #ccc;
  }
  .screen-list-text.done {
    text-decoration: line-through;
    color: var(--dim);
  }

  /* Swap table */
  .screen-swap {
    font-size: 0.65rem;
    margin-bottom: 8px;
  }
  .screen-swap-row {
    display: flex;
    gap: 6px;
    margin-bottom: 5px;
    align-items: stretch;
  }
  .screen-swap-bad {
    flex: 1;
    background: rgba(248,113,113,0.12);
    border: 1px solid rgba(248,113,113,0.2);
    border-radius: 6px;
    padding: 6px 7px;
    color: var(--coral);
    line-height: 1.3;
  }
  .screen-swap-good {
    flex: 1;
    background: rgba(41,187,137,0.12);
    border: 1px solid rgba(41,187,137,0.2);
    border-radius: 6px;
    padding: 6px 7px;
    color: var(--mint);
    line-height: 1.3;
  }
  .screen-swap-arrow {
    display: flex;
    align-items: center;
    color: var(--dim);
    font-size: 0.8rem;
    flex-shrink: 0;
  }

  /* Leaderboard */
  .screen-lb-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 8px;
    margin-bottom: 4px;
  }
  .screen-lb-row.highlight {
    background: rgba(41,187,137,0.1);
    border: 1px solid rgba(41,187,137,0.2);
  }
  .screen-lb-rank {
    font-size: 0.72rem;
    font-weight: 800;
    width: 18px;
    text-align: center;
    color: var(--dim);
  }
  .screen-lb-row.highlight .screen-lb-rank { color: var(--mint); }
  .screen-lb-name {
    flex: 1;
    font-size: 0.72rem;
    font-weight: 500;
    color: #ccc;
  }
  .screen-lb-row.highlight .screen-lb-name { color: #fff; font-weight: 700; }
  .screen-lb-score {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--muted);
  }
  .screen-lb-row.highlight .screen-lb-score { color: var(--mint); }

  /* Timer screen */
  .screen-timer {
    text-align: center;
    padding: 12px 0;
  }
  .screen-timer-ring {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    border: 5px solid rgba(255,255,255,0.08);
    border-top-color: var(--mint);
    border-right-color: var(--mint);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 8px;
    position: relative;
  }
  .screen-timer-val {
    font-size: 1.6rem;
    font-weight: 800;
    color: #fff;
  }
  .screen-timer-label {
    font-size: 0.68rem;
    color: var(--muted);
  }
  .screen-timer-phase {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 4px;
    display: inline-block;
    margin-top: 4px;
  }

  /* ============ MILESTONE BOX ============ */
  .milestone-box {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 28px;
    margin: 40px 0;
  }
  .milestone-box h3 {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 16px;
  }
  .milestone-box h3.p1 { color: var(--mint); }
  .milestone-box h3.p2 { color: var(--blue); }
  .milestone-box h3.p3 { color: var(--purple); }
  .milestone-box h3.p4 { color: var(--amber); }
  .milestone-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }
  .milestone-item {
    background: rgba(255,255,255,0.03);
    border-radius: 8px;
    padding: 14px;
    text-align: center;
  }
  .milestone-metric {
    font-size: 0.68rem;
    color: var(--dim);
    margin-bottom: 4px;
  }
  .milestone-target {
    font-size: 1.3rem;
    font-weight: 800;
  }
  .milestone-from {
    font-size: 0.68rem;
    color: var(--dim);
    margin-top: 2px;
  }

  /* ============ DIAGNOSIS SECTION ============ */
  .diagnosis {
    padding: 64px 0;
    border-bottom: 1px solid var(--border);
  }
  .diagnosis-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin: 24px 0;
  }
  .diag-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px;
  }
  .diag-card .diag-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 6px;
  }
  .diag-card .diag-val {
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 2px;
  }
  .diag-card .diag-context {
    font-size: 0.78rem;
    color: var(--dim);
  }

  /* ============ TRACKING TABLE ============ */
  .track-table {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;
    font-size: 0.85rem;
  }
  .track-table th {
    text-align: left;
    padding: 12px 14px;
    border-bottom: 2px solid var(--border);
    color: var(--dim);
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .track-table td {
    padding: 10px 14px;
    border-bottom: 1px solid rgba(51,65,85,0.4);
  }
  .track-table tr:hover td { background: rgba(41,187,137,0.03); }

  .tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
  }
  .tag-red { background: var(--coral-dim); color: var(--coral); }
  .tag-green { background: var(--mint-dim); color: var(--mint); }

  /* ============ FOOTER ============ */
  .footer {
    text-align: center;
    padding: 60px 24px;
    color: var(--dim);
    font-size: 0.82rem;
    border-top: 1px solid var(--border);
  }

  /* ============ RESPONSIVE ============ */
  @media (max-width: 900px) {
    .step-block { flex-direction: column; }
    .step-phone { position: static; align-self: center; margin-top: 24px; }
    .diagnosis-grid { grid-template-columns: 1fr; }
    .hero-stats { gap: 24px; }
  }
</style>
</head>
<body>

<!-- ==================== HERO ==================== -->
<div class="hero">
  <div class="hero-badge">Coaching Plan &middot; Confidential</div>
  <h1>Project Jess</h1>
  <div class="subtitle">A 90-day transformation plan to take Jessica from the bottom of the rankings to a top-10 server at Gen Korean BBQ</div>
  <div class="hero-stats">
    <div class="hero-stat">
      <div><span class="now">$57</span> <span class="arrow">&rarr;</span> <span class="goal">$95+</span></div>
      <div class="label">Average Order Value</div>
    </div>
    <div class="hero-stat">
      <div><span class="now">14</span> <span class="arrow">&rarr;</span> <span class="goal">60+</span></div>
      <div class="label">Performance Score</div>
    </div>
    <div class="hero-stat">
      <div><span class="now">50%</span> <span class="arrow">&rarr;</span> <span class="goal">90%+</span></div>
      <div class="label">Upsell Rate</div>
    </div>
    <div class="hero-stat">
      <div><span class="now">#34</span> <span class="arrow">&rarr;</span> <span class="goal">Top 10</span></div>
      <div class="label">Server Ranking</div>
    </div>
  </div>
</div>

<!-- ==================== NAV ==================== -->
<nav class="nav">
  <div class="nav-inner">
    <a href="#diagnosis">Diagnosis</a>
    <a href="#phase1">Phase 1: Quick Wins</a>
    <a href="#phase2">Phase 2: Playbook</a>
    <a href="#phase3">Phase 3: Level Up</a>
    <a href="#phase4">Phase 4: Compete</a>
    <a href="#wildcards">Wild Cards</a>
    <a href="#tracking">Tracking</a>
  </div>
</nav>

<div class="container">

<!-- ==================== DIAGNOSIS ==================== -->
<div class="diagnosis" id="diagnosis">
  <div class="phase-number p1">The Diagnosis</div>
  <h2 style="font-size:2rem; font-weight:800; letter-spacing:-0.03em; margin-bottom:8px;">Jessica isn't a lost cause. She's an untapped opportunity.</h2>
  <p style="color:var(--muted); max-width:700px; margin-bottom:24px;">14 shifts of data from Concord reveal a server whose potential is masked by correctable habits. Her Saturday AOV ($89) proves she <em>can</em> perform near the team average. The challenge is making every shift feel like Saturday.</p>

  <div class="diagnosis-grid">
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Current AOV</div>
      <div class="diag-val" style="color:var(--coral);">$57</div>
      <div class="diag-context">Team average is $98 &mdash; she's 42% below</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--amber);">Saturday AOV</div>
      <div class="diag-val" style="color:var(--amber);">$89</div>
      <div class="diag-context">Her 2 Saturday shifts prove near-average performance is possible</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Upsell Rate</div>
      <div class="diag-val" style="color:var(--coral);">50%</div>
      <div class="diag-context">Only half of greetings include any upsell attempt (vs. 90% for Armando)</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Says "Wagyu"</div>
      <div class="diag-val" style="color:var(--coral);">6%</div>
      <div class="diag-context">Top servers say it 42% of the time &mdash; 7x more often</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Pushes Pork/Shrimp</div>
      <div class="diag-val" style="color:var(--coral);">21%</div>
      <div class="diag-context">She defaults to low-AOV items. Pork correlates with -$9/order.</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Weekend Shifts</div>
      <div class="diag-val" style="color:var(--coral);">2 of 14</div>
      <div class="diag-context">86% of her shifts are Mon-Thu, missing the +$22 weekend premium</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">Asks "Been Here Before?"</div>
      <div class="diag-val" style="color:var(--coral);">38%</div>
      <div class="diag-context">Top servers ask 63%. It's the gateway to naming specific items.</div>
    </div>
    <div class="diag-card">
      <div class="diag-label" style="color:var(--coral);">AOV Trend</div>
      <div class="diag-val" style="color:var(--coral);">Declining</div>
      <div class="diag-context">$65 &rarr; $47 over 5 weeks. Getting worse without intervention.</div>
    </div>
  </div>
</div>

<!-- ==================== PHASE 1 ==================== -->
<div class="phase-header" id="phase1">
  <div class="phase-number p1">Phase 1</div>
  <h2>The Quick Wins</h2>
  <div class="phase-sub">Fix the easiest, highest-impact behaviors first. No new skills required &mdash; just awareness.</div>
  <div class="phase-dates">Days 1&ndash;14</div>
</div>

<!-- STEP 1 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p1">1</div>
    <div class="step-title">The Pre-Shift Ritual</div>
    <div class="step-body">
      <strong>Every shift, before clocking in, Jessica opens the Bespoke app and reviews her 3 "power items"</strong> &mdash; the specific dishes she'll name in every greeting that day. The list should always include <strong>wagyu</strong> and <strong>taco</strong> (the two highest-AOV items in the data), plus one rotating pick: tongue, short rib, or steak. She writes them on a sticky note in her server book. <strong>Never pork. Never fried rice. Never generic "shrimp."</strong>
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Jessica currently says "wagyu" in only 6% of greetings (top servers: 42%) and "taco" literally 0% of the time. Taco mentions correlate with <strong>+$17/order</strong> and wagyu with <strong>+$4/order</strong>. Just changing which words come out of her mouth is the single highest-leverage fix available.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">4:52 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Pre-Shift Prep</div>
          <div class="screen-title">Today's Power Items</div>
          <div class="screen-subtitle">Your 3 must-mention items for tonight</div>
        </div>
        <div class="screen-section">
          <div class="screen-card">
            <div class="screen-card-row">
              <div class="screen-card-icon" style="background:rgba(251,191,36,0.15); color:var(--amber);">&#127790;</div>
              <div class="screen-card-text">
                <div class="screen-card-title">Korean Taco Sampler</div>
                <div class="screen-card-sub">+$17 AOV impact &middot; Premium only</div>
              </div>
            </div>
          </div>
          <div class="screen-card">
            <div class="screen-card-row">
              <div class="screen-card-icon" style="background:rgba(248,113,113,0.15); color:var(--coral);">&#129385;</div>
              <div class="screen-card-text">
                <div class="screen-card-title">Wagyu Beef</div>
                <div class="screen-card-sub">+$4 AOV impact &middot; #1 seller</div>
              </div>
            </div>
          </div>
          <div class="screen-card">
            <div class="screen-card-row">
              <div class="screen-card-icon" style="background:rgba(96,165,250,0.15); color:var(--blue);">&#129360;</div>
              <div class="screen-card-text">
                <div class="screen-card-title">Marbled Short Rib</div>
                <div class="screen-card-sub">+$3 AOV impact &middot; Today's pick</div>
              </div>
            </div>
          </div>
        </div>
        <div class="screen-section">
          <div style="background:rgba(248,113,113,0.08); border:1px solid rgba(248,113,113,0.15); border-radius:8px; padding:8px 10px; font-size:0.62rem; color:var(--coral);">
            &#9888; Avoid tonight: pork, fried rice, plain shrimp
          </div>
        </div>
        <div class="screen-section">
          <div class="screen-btn mint">I'm Ready &mdash; Start My Shift</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 2 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p1">2</div>
    <div class="step-title">The 100% Rule</div>
    <div class="step-body">
      For the next two weeks, Jessica attempts an upsell mention in <strong>every single greeting</strong>, no exceptions. Even if it's a quick table, even if they're regulars, even if she's slammed. A 10-second mention counts: <em>"By the way, if you haven't tried the wagyu, it's incredible."</em> The goal isn't perfection &mdash; it's reps. Muscle memory builds through volume.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>She currently upsells in only 50% of greetings vs. Armando's 90%. Raising this to 80%+ should move the needle significantly &mdash; not because every pitch converts, but because consistency creates a habit that eventually becomes automatic.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">7:14 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Live Shift Tracker</div>
          <div class="screen-title">Tonight's Upsell Streak</div>
        </div>
        <div class="screen-section" style="text-align:center;">
          <div style="font-size:3rem; font-weight:900; color:var(--mint);">5/5</div>
          <div style="font-size:0.72rem; color:var(--muted); margin-bottom:4px;">Greetings with upsell attempt</div>
          <div style="font-size:0.65rem; font-weight:700; color:var(--mint); background:var(--mint-dim); display:inline-block; padding:3px 12px; border-radius:12px;">&#128293; Perfect streak!</div>
        </div>
        <div class="screen-section">
          <div class="screen-list-item">
            <div class="screen-check done">&#10003;</div>
            <div class="screen-list-text done">Table 12 &mdash; Mentioned wagyu</div>
          </div>
          <div class="screen-list-item">
            <div class="screen-check done">&#10003;</div>
            <div class="screen-list-text done">Table 8 &mdash; Mentioned taco sampler</div>
          </div>
          <div class="screen-list-item">
            <div class="screen-check done">&#10003;</div>
            <div class="screen-list-text done">Table 15 &mdash; Described short rib</div>
          </div>
          <div class="screen-list-item">
            <div class="screen-check done">&#10003;</div>
            <div class="screen-list-text done">Table 3 &mdash; Premium pitch</div>
          </div>
          <div class="screen-list-item">
            <div class="screen-check done">&#10003;</div>
            <div class="screen-list-text done">Table 19 &mdash; Wagyu + tacos</div>
          </div>
          <div class="screen-list-item">
            <div class="screen-check" style="border-color:rgba(255,255,255,0.3);"></div>
            <div class="screen-list-text" style="color:#fff;">Table 7 &mdash; Next up...</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 3 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p1">3</div>
    <div class="step-title">The "Been Here Before?" Habit</div>
    <div class="step-body">
      Jessica starts every single greeting with <strong>"Have you been here before?"</strong> before anything else. If yes &rarr; <em>"Have you tried the premium menu? The wagyu is our number one."</em> If no &rarr; she has a natural opening to explain the menu and steer toward premium. This one question is the bridge to everything else.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Jessica asks this only 38% of the time vs. 63% for top servers. When servers ask this question, they name specific items 50% of the time vs. 32% when they skip it. It's the behavioral gateway &mdash; the question that opens the door to an upsell.</p>
    </div>
  </div>
</div>

<!-- STEP 4 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p1">4</div>
    <div class="step-title">The Language Swap Card</div>
    <div class="step-body">
      Jessica gets a laminated card (or saves a screenshot on her phone) with exact phrases to swap. The left column is what she says now. The right column is what top servers say instead. She keeps it in her apron pocket and glances at it before approaching each table until the new language becomes automatic.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Her transcripts show she leads with price (<em>"$20 per person"</em>) and pushes shrimp/pork. Top servers lead with value and push wagyu/taco. This isn't personality &mdash; it's vocabulary. Vocabulary can be trained.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">5:30 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Quick Reference</div>
          <div class="screen-title">Language Swap Card</div>
          <div class="screen-subtitle">Replace old habits with winning phrases</div>
        </div>
        <div class="screen-section">
          <div style="display:flex; gap:4px; margin-bottom:8px;">
            <div style="flex:1; font-size:0.58rem; font-weight:700; color:var(--coral); text-align:center;">STOP SAYING</div>
            <div style="width:16px;"></div>
            <div style="flex:1; font-size:0.58rem; font-weight:700; color:var(--mint); text-align:center;">SAY THIS</div>
          </div>
          <div class="screen-swap">
            <div class="screen-swap-row">
              <div class="screen-swap-bad">"Want to upgrade to premium?"</div>
              <div class="screen-swap-arrow">&rarr;</div>
              <div class="screen-swap-good">"Our regulars go premium &mdash; the wagyu is #1"</div>
            </div>
            <div class="screen-swap-row">
              <div class="screen-swap-bad">"It's $20 per person extra"</div>
              <div class="screen-swap-arrow">&rarr;</div>
              <div class="screen-swap-good">"Unlimited wagyu and tacos &mdash; totally worth it"</div>
            </div>
            <div class="screen-swap-row">
              <div class="screen-swap-bad">"Want to try the Cajun shrimp?"</div>
              <div class="screen-swap-arrow">&rarr;</div>
              <div class="screen-swap-good">"The Korean taco sampler is insane &mdash; people go crazy"</div>
            </div>
            <div class="screen-swap-row">
              <div class="screen-swap-bad">"We have a premium menu"</div>
              <div class="screen-swap-arrow">&rarr;</div>
              <div class="screen-swap-good">"The premium is where the good stuff is"</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 5 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p1">5</div>
    <div class="step-title">Shadow Shift with Armando</div>
    <div class="step-body">
      Jessica shadows <strong>Armando Grona</strong> (the #1 ranked server) for one full shift with no tables of her own. She watches how he greets every table, what words he uses, how he reads the room, and how he handles objections. She takes notes. Afterwards, they debrief for 15 minutes over a staff meal. Reading about technique is one thing &mdash; watching the best do it table after table makes it visceral.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Armando mentions wagyu in 42% of greetings, asks "been here before?" 63% of the time, and upsells in 90% of greetings. His style is casual, confident, and natural. Seeing it in person is worth more than any manual.</p>
    </div>
  </div>
</div>

<!-- MILESTONE 1 -->
<div class="milestone-box">
  <h3 class="p1">Phase 1 Milestones &mdash; Day 14 Check-In</h3>
  <div class="milestone-grid">
    <div class="milestone-item">
      <div class="milestone-metric">Upsell Attempt Rate</div>
      <div class="milestone-target" style="color:var(--mint);">80%+</div>
      <div class="milestone-from">from 50%</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">"Wagyu" Mention Rate</div>
      <div class="milestone-target" style="color:var(--mint);">25%+</div>
      <div class="milestone-from">from 6%</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Average Order Value</div>
      <div class="milestone-target" style="color:var(--mint);">$65+</div>
      <div class="milestone-from">from $57</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">"Been Here Before?" Rate</div>
      <div class="milestone-target" style="color:var(--mint);">55%+</div>
      <div class="milestone-from">from 38%</div>
    </div>
  </div>
</div>

<!-- ==================== PHASE 2 ==================== -->
<div class="phase-header" id="phase2">
  <div class="phase-number p2">Phase 2</div>
  <h2>Build the Playbook</h2>
  <div class="phase-sub">Deepen the technique, add drink promos, and start earning better shifts.</div>
  <div class="phase-dates">Days 15&ndash;45</div>
</div>

<!-- STEP 6 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">6</div>
    <div class="step-title">The 60-Second Greeting Structure</div>
    <div class="step-body">
      Jessica learns a structured greeting that uses her natural warmth <em>plus</em> a deliberate upsell arc. She practices it at home, with her manager, and then on her first 3 tables each shift until it's automatic. The structure is flexible &mdash; not a word-for-word script &mdash; but it ensures she hits every beat.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Her greetings average 103 words / 61 seconds but are unfocused &mdash; a mix of logistics and scattered mentions. Top servers use similar time but pack it with specific item names and descriptions. Structure, not length, is the fix.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">5:15 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Greeting Coach</div>
          <div class="screen-title">60-Second Structure</div>
        </div>
        <div class="screen-section">
          <div class="screen-timer">
            <div class="screen-timer-ring">
              <div class="screen-timer-val">0:42</div>
            </div>
          </div>
        </div>
        <div class="screen-section">
          <div class="screen-card" style="border-left:3px solid var(--mint); opacity:0.5;">
            <div class="screen-card-title" style="font-size:0.68rem;">&#10003; 0&ndash;10s: Warm Up</div>
            <div class="screen-card-sub">"Welcome! Been here before?"</div>
          </div>
          <div class="screen-card" style="border-left:3px solid var(--mint); opacity:0.5;">
            <div class="screen-card-title" style="font-size:0.68rem;">&#10003; 10&ndash;30s: Frame Premium</div>
            <div class="screen-card-sub">"The premium menu is where the good stuff is"</div>
          </div>
          <div class="screen-card" style="border-left:3px solid var(--blue);">
            <div class="screen-card-title" style="font-size:0.68rem;">&#9654; 30&ndash;50s: Name &amp; Describe</div>
            <div class="screen-card-sub">"The wagyu is our #1 &mdash; insanely tender. And the Korean taco sampler is my favorite."</div>
          </div>
          <div class="screen-card" style="border-left:3px solid var(--dim); opacity:0.4;">
            <div class="screen-card-title" style="font-size:0.68rem;">&#9711; 50&ndash;60s: Close</div>
            <div class="screen-card-sub">"Most tables go premium &mdash; want me to set you up?"</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 7 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">7</div>
    <div class="step-title">The Drink Promo Blitz</div>
    <div class="step-body">
      Jessica sets a personal goal of <strong>3 drink promotions per shift</strong> (up from 1.1). She picks one drink to push each shift &mdash; soju cocktails, beer specials, whatever's running &mdash; and mentions it to every table at least once. Drinks prime customers for premium food decisions and carry high margins.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Shifts with 3+ drink promos average <strong>$838 revenue</strong> vs. $654 for zero-promo shifts. Jessica's 1.1 drink promos/shift is well below the team average of 1.9. This is low-hanging fruit.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">8:20 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Drink Promo Goal</div>
          <div class="screen-title">Tonight's Target: 3</div>
        </div>
        <div class="screen-section" style="text-align:center;">
          <div style="display:inline-flex; gap:12px; margin:8px 0 16px;">
            <div style="width:42px; height:42px; border-radius:50%; background:var(--blue); display:flex; align-items:center; justify-content:center; font-size:1.1rem;">&#127867;</div>
            <div style="width:42px; height:42px; border-radius:50%; background:var(--blue); display:flex; align-items:center; justify-content:center; font-size:1.1rem;">&#127867;</div>
            <div style="width:42px; height:42px; border-radius:50%; background:rgba(255,255,255,0.08); border:2px dashed rgba(255,255,255,0.2); display:flex; align-items:center; justify-content:center; font-size:0.8rem; color:var(--dim);">3</div>
          </div>
          <div style="font-size:0.72rem; color:var(--muted);">2 of 3 drink promos completed</div>
        </div>
        <div class="screen-section">
          <div class="screen-card">
            <div class="screen-card-title" style="font-size:0.72rem;">Tonight's Featured Drink</div>
            <div style="font-size:0.9rem; font-weight:700; color:var(--blue); margin:4px 0;">Soju Watermelon Cocktail</div>
            <div class="screen-card-sub">"We've got this amazing watermelon soju cocktail tonight &mdash; it's super refreshing with the BBQ"</div>
          </div>
        </div>
        <div class="screen-section">
          <div class="screen-stat-row">
            <span class="screen-stat-label">Your avg drink promos</span>
            <span class="screen-stat-val" style="color:var(--coral);">1.1/shift</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Team average</span>
            <span class="screen-stat-val" style="color:var(--amber);">1.9/shift</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Top servers</span>
            <span class="screen-stat-val" style="color:var(--mint);">3.2/shift</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 8 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">8</div>
    <div class="step-title">The "Describe, Don't List" Exercise</div>
    <div class="step-body">
      Once a week, Jessica picks one menu item she's never described to a table and writes a <strong>2-sentence description</strong>: taste, texture, what it goes well with, why she personally likes it. She practices saying it out loud. Then she uses it on every table that shift.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Her "describe specific item" rate is 11% of greetings &mdash; nearly zero at the shift level. Top servers describe items because it makes recommendations feel personal and genuine, not transactional. You can't describe what you've never thought about.</p>
    </div>
  </div>
</div>

<!-- STEP 9 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">9</div>
    <div class="step-title">Eat the Menu</div>
    <div class="step-body">
      Jessica does a <strong>company-paid tasting session</strong> of every premium item she hasn't tried, especially: wagyu, Korean taco sampler, marbled short rib, tongue, and rib eye. She writes one sentence about each that she could say to a customer. It's hard to sound passionate about wagyu if you've never eaten it.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Several of her transcripts mention shrimp as "the only thing you can get" on premium &mdash; suggesting she may not be familiar with the rest of the premium menu. First-hand experience makes recommendations authentic. Customers can tell the difference.</p>
    </div>
  </div>
</div>

<!-- STEP 10 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">10</div>
    <div class="step-title">Request 1 Weekend Shift Per Week</div>
    <div class="step-body">
      Jessica (or her manager) adjusts her schedule to include at least <strong>one Friday, Saturday, or Sunday shift per week</strong>. Currently 12 of her 14 shifts are Mon&ndash;Thu. Weekend shifts boost her AOV through larger parties and occasion dining, and let her practice in a higher-energy environment.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>The weekend AOV premium is +$22 across the board. Jessica's own Saturday AOV is <strong>$89</strong> vs. $52 on weekdays. Just one weekend shift per week would significantly lift her average &mdash; and higher-energy tables are actually easier to upsell to.</p>
    </div>
  </div>
</div>

<!-- STEP 11 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p2">11</div>
    <div class="step-title">Weekly Bespoke Score Review</div>
    <div class="step-body">
      Every Monday, Jessica opens her Bespoke dashboard and reviews her scores from the past week. She identifies her <strong>best greeting</strong> and her <strong>worst greeting</strong>, reads both transcripts, and writes down one thing she'll do differently. This 5-minute ritual breaks the decline cycle.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Jessica's AOV is declining week-over-week ($65 &rarr; $47). Without self-awareness, the slide continues. Regular review creates accountability, surfaces patterns she wouldn't otherwise notice, and lets her celebrate small wins.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">10:30 AM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Monday Review</div>
          <div class="screen-title">Last Week's Scores</div>
          <div class="screen-subtitle">Week of May 5 &middot; 3 shifts</div>
        </div>
        <div class="screen-section">
          <div class="screen-bar-row">
            <div class="screen-bar-label">AOV</div>
            <div class="screen-bar-track"><div class="screen-bar-fill" style="width:58%; background: linear-gradient(90deg, var(--coral), var(--amber));"></div></div>
            <div class="screen-bar-val" style="color:var(--amber);">$68</div>
          </div>
          <div class="screen-bar-row">
            <div class="screen-bar-label">Upsell</div>
            <div class="screen-bar-track"><div class="screen-bar-fill" style="width:72%; background: linear-gradient(90deg, var(--amber), var(--mint));"></div></div>
            <div class="screen-bar-val" style="color:var(--mint);">72%</div>
          </div>
          <div class="screen-bar-row">
            <div class="screen-bar-label">Wagyu</div>
            <div class="screen-bar-track"><div class="screen-bar-fill" style="width:30%; background: var(--blue);"></div></div>
            <div class="screen-bar-val" style="color:var(--blue);">30%</div>
          </div>
          <div class="screen-bar-row">
            <div class="screen-bar-label">Drinks</div>
            <div class="screen-bar-track"><div class="screen-bar-fill" style="width:50%; background: var(--purple);"></div></div>
            <div class="screen-bar-val" style="color:var(--purple);">2.3</div>
          </div>
        </div>
        <div class="screen-section">
          <div class="screen-card" style="border-left:3px solid var(--mint);">
            <div class="screen-card-sub" style="color:var(--mint); font-weight:600;">&#11088; Best Greeting</div>
            <div class="screen-card-sub">Sat 8:15 PM &mdash; Table 12<br>"The wagyu is our #1... taco sampler is my favorite"</div>
          </div>
          <div class="screen-card" style="border-left:3px solid var(--coral); margin-top:6px;">
            <div class="screen-card-sub" style="color:var(--coral); font-weight:600;">&#128269; Needs Work</div>
            <div class="screen-card-sub">Wed 6:30 PM &mdash; Table 5<br>No upsell attempt, jumped to logistics</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- MILESTONE 2 -->
<div class="milestone-box">
  <h3 class="p2">Phase 2 Milestones &mdash; Day 45 Check-In</h3>
  <div class="milestone-grid">
    <div class="milestone-item">
      <div class="milestone-metric">Upsell Rate</div>
      <div class="milestone-target" style="color:var(--blue);">85%+</div>
      <div class="milestone-from">from 80%</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Average Order Value</div>
      <div class="milestone-target" style="color:var(--blue);">$75+</div>
      <div class="milestone-from">from $65</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Drink Promos/Shift</div>
      <div class="milestone-target" style="color:var(--blue);">2.5+</div>
      <div class="milestone-from">from 1.1</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Describe-Item Rate</div>
      <div class="milestone-target" style="color:var(--blue);">25%+</div>
      <div class="milestone-from">from 11%</div>
    </div>
  </div>
</div>

<!-- ==================== PHASE 3 ==================== -->
<div class="phase-header" id="phase3">
  <div class="phase-number p3">Phase 3</div>
  <h2>Level Up</h2>
  <div class="phase-sub">Go beyond scripted moves. Read tables, personalize, and start competing.</div>
  <div class="phase-dates">Days 46&ndash;75</div>
</div>

<!-- STEP 12 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">12</div>
    <div class="step-title">Table Reading Masterclass</div>
    <div class="step-body">
      Jessica attends a 1-hour session on <strong>reading table dynamics</strong>: group size, age mix, occasion (birthday, date night, friends), and alcohol ordering as signals for how aggressively to push premium. Larger celebrating groups &rarr; go all-in. Quiet couple on a budget &rarr; softer touch. The best servers match pitch intensity to the customer.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>The "mystery servers" analysis showed that great upsell scores don't always translate to high AOV. Some servers push premium on every table regardless of context. Learning when to turn the dial up vs. down is what separates good from great.</p>
    </div>
  </div>
</div>

<!-- STEP 13 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">13</div>
    <div class="step-title">Company-Paid Sales Training</div>
    <div class="step-body">
      Jessica attends a half-day <strong>hospitality upselling workshop</strong> or completes an online course (suggestive selling, guest engagement, or a ServSafe Plus equivalent). The company pays for the course and for her time. Professional training reframes upselling as a <em>career skill</em>, not just a task.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>External training signals that the company is investing in Jessica's growth. It builds loyalty, motivation, and gives her a credential. People work harder on skills they've been formally trained in.</p>
    </div>
  </div>
</div>

<!-- STEP 14 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">14</div>
    <div class="step-title">The Personal Touch Experiment</div>
    <div class="step-body">
      For one week, Jessica adds one <strong>personal element</strong> to every greeting: remembering a regular's name, commenting on a birthday pin, asking about their day, or sharing her own genuine favorite: <em>"Honestly? When I eat here, I always get the wagyu and taco sampler."</em> She tracks whether it feels natural and whether customers respond differently.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>The "natural seller" servers barely follow the upsell script but pull $126+ AOV through rapport and trust. Jessica's transcripts show she already has a warm conversational style &mdash; she just needs to lean into it more instead of rushing through logistics.</p>
    </div>
  </div>
</div>

<!-- STEP 15 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">15</div>
    <div class="step-title">Concord Upsell Challenge</div>
    <div class="step-body">
      Launch a <strong>friendly weekly competition</strong> among Concord servers: whoever has the highest upsell quality score that week gets a small prize (gift card, first pick of schedule, bragging rights on a visible board). Jessica participates alongside her peers. Public scorecards create positive accountability.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Gamification creates peer pressure that's positive rather than punitive. Knowing her scores are visible &mdash; not just to management but to fellow servers &mdash; is a powerful motivator. And celebrating the weekly winner normalizes upselling as something to be proud of.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">3:45 PM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Weekly Challenge</div>
          <div class="screen-title">&#127942; Concord Leaderboard</div>
          <div class="screen-subtitle">Week of June 2 &middot; Upsell Quality Score</div>
        </div>
        <div class="screen-section">
          <div class="screen-lb-row" style="background:rgba(251,191,36,0.08);">
            <div class="screen-lb-rank" style="color:var(--amber);">1</div>
            <div class="screen-lb-name">&#127942; Armando G.</div>
            <div class="screen-lb-score" style="color:var(--amber);">84%</div>
          </div>
          <div class="screen-lb-row">
            <div class="screen-lb-rank">2</div>
            <div class="screen-lb-name">Schiell P.</div>
            <div class="screen-lb-score">71%</div>
          </div>
          <div class="screen-lb-row">
            <div class="screen-lb-rank">3</div>
            <div class="screen-lb-name">Vincent O.</div>
            <div class="screen-lb-score">65%</div>
          </div>
          <div class="screen-lb-row highlight">
            <div class="screen-lb-rank">4</div>
            <div class="screen-lb-name">&#11088; Jess C. <span style="font-size:0.58rem; color:var(--mint); font-weight:400;">&nbsp;+12 spots!</span></div>
            <div class="screen-lb-score">62%</div>
          </div>
          <div class="screen-lb-row">
            <div class="screen-lb-rank">5</div>
            <div class="screen-lb-name">Damien A.</div>
            <div class="screen-lb-score">48%</div>
          </div>
          <div class="screen-lb-row">
            <div class="screen-lb-rank">6</div>
            <div class="screen-lb-name">Ricardo C.</div>
            <div class="screen-lb-score">41%</div>
          </div>
        </div>
        <div class="screen-section" style="text-align:center;">
          <div style="font-size:0.62rem; color:var(--mint); font-weight:600;">&#127881; Jess moved up 12 spots this week!</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- STEP 16 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">16</div>
    <div class="step-title">The Buddy System</div>
    <div class="step-body">
      Pair Jessica with <strong>Vincent Oriarte</strong> &mdash; a mid-tier server trending +$18/week who's recently improved. They check in before each shift: <em>"What are your three items today? Did you review your Bespoke scores?"</em> Having a peer (not a boss) as an accountability partner feels supportive rather than surveillant.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Vincent is on the steepest improvement arc in the data. He's not a "natural" like Armando &mdash; he got better through effort. That makes him a more relatable role model. And improving servers helping each other builds real team culture.</p>
    </div>
  </div>
</div>

<!-- STEP 17 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p3">17</div>
    <div class="step-title">Roleplay Wednesdays</div>
    <div class="step-body">
      Once a week, Jessica and 2&ndash;3 other servers spend <strong>10 minutes before their shift roleplaying</strong> greeting scenarios. One person plays the customer, another the server. They try different approaches, give feedback, and laugh about it. Keep it light and fun &mdash; this isn't a test.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Practice in a low-stakes environment reduces anxiety about trying new language on real tables. Jessica's transcripts show she defaults to logistics when under pressure &mdash; roleplay builds confidence so the upsell language comes out naturally when it matters.</p>
    </div>
  </div>
</div>

<!-- MILESTONE 3 -->
<div class="milestone-box">
  <h3 class="p3">Phase 3 Milestones &mdash; Day 75 Check-In</h3>
  <div class="milestone-grid">
    <div class="milestone-item">
      <div class="milestone-metric">Upsell Rate</div>
      <div class="milestone-target" style="color:var(--purple);">90%+</div>
      <div class="milestone-from">from 85%</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Average Order Value</div>
      <div class="milestone-target" style="color:var(--purple);">$85+</div>
      <div class="milestone-from">from $75</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">At Least One Shift</div>
      <div class="milestone-target" style="color:var(--purple);">$100+ AOV</div>
      <div class="milestone-from">current best: $102</div>
    </div>
    <div class="milestone-item">
      <div class="milestone-metric">Performance Score</div>
      <div class="milestone-target" style="color:var(--purple);">40+</div>
      <div class="milestone-from">from 14</div>
    </div>
  </div>
</div>

<!-- ==================== PHASE 4 ==================== -->
<div class="phase-header" id="phase4">
  <div class="phase-number p4">Phase 4</div>
  <h2>Compete for the Top</h2>
  <div class="phase-sub">Polish, sustain, and push for a top-10 ranking.</div>
  <div class="phase-dates">Days 76&ndash;90</div>
</div>

<!-- STEP 18 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p4">18</div>
    <div class="step-title">The "One Great Shift" Challenge</div>
    <div class="step-body">
      Jessica sets a personal goal: <strong>one shift where her AOV exceeds $110</strong> (the team's Saturday average). She picks her best upcoming Saturday, preps her power items, reviews the greeting structure, and goes all in. Hit it once and she knows it's possible. Then she does it again.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Psychological proof. Once you've done it, the "I can't" narrative dies. Her current best is $102.49 on a Saturday &mdash; she's closer than she thinks. One great shift changes her self-image from "struggling server" to "server who can hit $110."</p>
    </div>
  </div>
</div>

<!-- STEP 19 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p4">19</div>
    <div class="step-title">Become the Trainer</div>
    <div class="step-body">
      Once Jessica's scores are consistently above team average, she helps <strong>train a newer server</strong> on the greeting structure. Teaching solidifies learning. <em>"I used to push shrimp instead of wagyu &mdash; here's what changed for me."</em> This repositions Jessica from "the one who needs coaching" to "the one who helps others."
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Teaching is the deepest form of learning. Explaining <em>why</em> something works forces you to internalize it at a level that just doing it never reaches. It also builds team culture &mdash; this is the team-building multiplier you mentioned.</p>
    </div>
  </div>
</div>

<!-- STEP 20 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num p4">20</div>
    <div class="step-title">The 90-Day Celebration</div>
    <div class="step-body">
      At the 90-day mark, Jessica and her manager sit down with her <strong>full Bespoke trajectory</strong>: where she started, where she is now, the specific behaviors that changed. They celebrate the wins, identify what's left, and set new 90-day goals. If she's hit her targets, she earns a visible reward &mdash; schedule upgrade, raise discussion, or public recognition.
    </div>
    <div class="why-box">
      <div class="why-label">Why this works</div>
      <p>Closure and recognition complete the loop. Without a formal celebration, improvement fades into noise. Seeing her own transformation visualized &mdash; $57 &rarr; $95+ &mdash; is the most powerful motivator for the next 90 days.</p>
    </div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">11:00 AM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">90-Day Review</div>
          <div class="screen-title">&#127881; Jess's Transformation</div>
        </div>
        <div class="screen-section" style="text-align:center;">
          <div style="font-size:2.4rem; font-weight:900; color:var(--mint); line-height:1;">$97</div>
          <div style="font-size:0.68rem; color:var(--muted); margin-bottom:4px;">Current AOV</div>
          <div style="font-size:0.62rem; color:var(--dim);">Started at $57 &middot; +70% improvement</div>
        </div>
        <div class="screen-section">
          <div class="screen-stat-row">
            <span class="screen-stat-label">Upsell Rate</span>
            <span class="screen-stat-val" style="color:var(--mint);">50% &rarr; 91%</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Wagyu Mentions</span>
            <span class="screen-stat-val" style="color:var(--mint);">6% &rarr; 42%</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Drink Promos</span>
            <span class="screen-stat-val" style="color:var(--mint);">1.1 &rarr; 3.0</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Ranking</span>
            <span class="screen-stat-val" style="color:var(--mint);">#34 &rarr; #8</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Best Shift AOV</span>
            <span class="screen-stat-val" style="color:var(--amber);">$118 &#11088;</span>
          </div>
        </div>
        <div class="screen-section">
          <div style="background:var(--mint-dim); border:1px solid rgba(41,187,137,0.2); border-radius:8px; padding:10px; text-align:center;">
            <div style="font-size:0.72rem; font-weight:700; color:var(--mint);">+$259/month in tips</div>
            <div style="font-size:0.6rem; color:var(--muted);">From AOV improvement alone</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ==================== WILD CARDS ==================== -->
<div class="phase-header" id="wildcards">
  <div class="phase-number wild">Bonus</div>
  <h2>The Wild Card Ideas</h2>
  <div class="phase-sub">Creative options to deploy anytime during the 90 days. Use the ones that fit Jessica's personality.</div>
</div>

<!-- WILD 21 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">21</div>
    <div class="step-title">Mystery Diner Feedback</div>
    <div class="step-body">Send a friend or family member of a manager to sit at one of Jessica's tables as a mystery diner. They score her greeting, upsell attempt, and overall warmth on a simple rubric and give her feedback the next day. Objective outside feedback hits differently than a manager's notes.</div>
  </div>
</div>

<!-- WILD 22 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">22</div>
    <div class="step-title">Audio Self-Review</div>
    <div class="step-body">If Bespoke transcripts include audio, have Jessica <strong>listen to her own best greeting and worst greeting side-by-side</strong>. Self-awareness through hearing your own voice is incredibly effective. Most people have never heard themselves sell.</div>
  </div>
</div>

<!-- WILD 23 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">23</div>
    <div class="step-title">The "Wagyu Challenge" Incentive</div>
    <div class="step-body">For one month, <strong>any server who gets their wagyu-mention rate above 40%</strong> earns a $50 bonus. This targets Jessica's single biggest gap with a direct financial incentive &mdash; and it lifts the whole team.</div>
  </div>
</div>

<!-- WILD 24 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">24</div>
    <div class="step-title">Cross-Location Visit</div>
    <div class="step-body">Send Jessica to <strong>work one shift at Fremont</strong>. Different environment, different pace, different servers. Cross-pollination sparks new ideas and breaks the "this is just how I do it at Concord" routine. She might pick up techniques from Fremont servers she'd never encounter otherwise.</div>
  </div>
</div>

<!-- WILD 25 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">25</div>
    <div class="step-title">Make It Personal: The Tips Calculator</div>
    <div class="step-body">Help Jessica calculate what even a $20 AOV increase means for her take-home. At 18% average tip &times; 6 tables/shift &times; 12 shifts/month: that's <strong>+$259/month in her pocket</strong>. Over a year, that's $3,100. Make the abstract concrete.</div>
  </div>
  <div class="step-phone">
    <div class="phone">
      <div class="phone-notch"></div>
      <div class="phone-screen">
        <div class="phone-status">
          <span class="time">9:15 AM</span>
          <span class="icons">
            <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
            <svg viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </span>
        </div>
        <div class="app-header">
          <div class="app-logo">B</div>
          <div class="app-name">Bespoke</div>
        </div>
        <div class="screen-section">
          <div class="screen-label">Tips Calculator</div>
          <div class="screen-title">What's +$20 AOV Worth?</div>
        </div>
        <div class="screen-section" style="text-align:center;">
          <div style="font-size:2.4rem; font-weight:900; color:var(--mint); line-height:1;">+$259</div>
          <div style="font-size:0.78rem; color:var(--muted);">extra per month in tips</div>
        </div>
        <div class="screen-section">
          <div class="screen-stat-row">
            <span class="screen-stat-label">AOV increase</span>
            <span class="screen-stat-val" style="color:#fff;">+$20/order</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Avg tip rate</span>
            <span class="screen-stat-val" style="color:#fff;">18%</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Tables per shift</span>
            <span class="screen-stat-val" style="color:#fff;">~6</span>
          </div>
          <div class="screen-stat-row">
            <span class="screen-stat-label">Shifts per month</span>
            <span class="screen-stat-val" style="color:#fff;">~12</span>
          </div>
        </div>
        <div class="screen-section">
          <div style="background:rgba(251,191,36,0.08); border:1px solid rgba(251,191,36,0.15); border-radius:10px; padding:12px; text-align:center;">
            <div style="font-size:0.68rem; color:var(--dim); margin-bottom:2px;">That's</div>
            <div style="font-size:1.4rem; font-weight:800; color:var(--amber);">$3,108/year</div>
            <div style="font-size:0.62rem; color:var(--dim); margin-top:2px;">just from saying different words</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- WILD 26-30 -->
<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">26</div>
    <div class="step-title">Video Study: Top Server Compilations</div>
    <div class="step-body">Create a <strong>5-minute highlight reel</strong> of Armando's best greeting transcripts (or audio if available) that Jessica can listen to on her commute. Repetition breeds familiarity. Hearing the rhythm and confidence of a top server's pitch is a form of passive training.</div>
  </div>
</div>

<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">27</div>
    <div class="step-title">The "No Pork, No Fried Rice" Challenge</div>
    <div class="step-body">For two weeks, Jessica is specifically challenged to <strong>never mention pork or fried rice in a greeting</strong>. Every instance gets replaced with wagyu or taco. Track compliance through Bespoke transcripts. Eliminating negative-AOV items is as important as adding positive ones.</div>
  </div>
</div>

<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">28</div>
    <div class="step-title">Menu Engineering Session</div>
    <div class="step-body">Jessica sits down with the kitchen manager for 20 minutes and <strong>learns the actual food cost and margin on premium items</strong>. Knowing that wagyu is a profit center (not just a customer favorite) adds conviction to her pitch. Understanding the business behind the food changes the framing.</div>
  </div>
</div>

<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">29</div>
    <div class="step-title">Shift-Start Visualization</div>
    <div class="step-body">Before walking onto the floor, Jessica spends <strong>30 seconds visualizing</strong> her first table greeting going perfectly &mdash; the customer nodding, choosing premium, her feeling confident. Sports psychology adapted for hospitality. Visualization primes the brain for success.</div>
  </div>
</div>

<div class="step-block">
  <div class="step-content">
    <div class="step-num wild">30</div>
    <div class="step-title">The "Saturday Jessica" Mindset</div>
    <div class="step-body">The data shows Jessica performs dramatically better on Saturdays (<strong>$89 AOV vs. $52 weekday</strong>). What's different? Energy? Customer mix? Her own mood? Help her identify what's different about her Saturday self and <em>bottle it</em> for Tuesday. The best version of Jessica already exists &mdash; she just needs to show up every shift.</div>
  </div>
</div>

<!-- ==================== TRACKING ==================== -->
<div class="phase-header" id="tracking">
  <div class="phase-number p1">Accountability</div>
  <h2>90-Day Tracking Dashboard</h2>
  <div class="phase-sub">Every metric has a current baseline, intermediate targets, and a 90-day goal.</div>
</div>

<table class="track-table">
  <thead>
    <tr>
      <th>Metric</th>
      <th>Current</th>
      <th>Day 14</th>
      <th>Day 45</th>
      <th>Day 75</th>
      <th>Day 90 Goal</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>Average Order Value</strong></td>
      <td><span class="tag tag-red">$57</span></td>
      <td>$65</td>
      <td>$75</td>
      <td>$85</td>
      <td><span class="tag tag-green">$95+</span></td>
    </tr>
    <tr>
      <td><strong>Performance Score</strong></td>
      <td><span class="tag tag-red">14</span></td>
      <td>20</td>
      <td>30</td>
      <td>40</td>
      <td><span class="tag tag-green">60+</span></td>
    </tr>
    <tr>
      <td><strong>Upsell Attempt Rate</strong></td>
      <td><span class="tag tag-red">50%</span></td>
      <td>80%</td>
      <td>85%</td>
      <td>90%</td>
      <td><span class="tag tag-green">90%+</span></td>
    </tr>
    <tr>
      <td><strong>"Wagyu" Mention Rate</strong></td>
      <td><span class="tag tag-red">6%</span></td>
      <td>25%</td>
      <td>35%</td>
      <td>40%</td>
      <td><span class="tag tag-green">40%+</span></td>
    </tr>
    <tr>
      <td><strong>Drink Promos / Shift</strong></td>
      <td><span class="tag tag-red">1.1</span></td>
      <td>1.5</td>
      <td>2.5</td>
      <td>3.0</td>
      <td><span class="tag tag-green">3.0+</span></td>
    </tr>
    <tr>
      <td><strong>"Been Here Before?" Rate</strong></td>
      <td><span class="tag tag-red">38%</span></td>
      <td>55%</td>
      <td>65%</td>
      <td>70%</td>
      <td><span class="tag tag-green">70%+</span></td>
    </tr>
    <tr>
      <td><strong>Weekend Shifts / Week</strong></td>
      <td><span class="tag tag-red">0.5</span></td>
      <td>1.0</td>
      <td>1.0</td>
      <td>1.0</td>
      <td><span class="tag tag-green">1.0+</span></td>
    </tr>
    <tr>
      <td><strong>Describe-Item Rate</strong></td>
      <td><span class="tag tag-red">11%</span></td>
      <td>15%</td>
      <td>25%</td>
      <td>30%</td>
      <td><span class="tag tag-green">30%+</span></td>
    </tr>
  </tbody>
</table>

<div style="padding-bottom:40px;"></div>

</div>

<!-- ==================== FOOTER ==================== -->
<div class="footer">
  <p style="margin-bottom:8px;"><strong style="color:var(--text);">Project Jess</strong> &mdash; 90-Day Star Server Transformation</p>
  <p>Prepared by Bespoke AI &middot; Data: Gen Korean BBQ Concord &middot; April&ndash;May 2025 &middot; 14 shifts analyzed</p>
</div>

</body>
</html>
`;

export default jessCoachingPlanHtml;
